import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import SweetAlert from 'sweetalert2';
import { AlertCircle, Check, Save, X } from 'react-feather';

import config from '../../../../config';

import useTenantLookups, { invalidateTenantLookups } from '../shared/useTenantLookups';
import BrandSection, { BRAND_FIELDS } from '../shared/BrandSection';
import UnitSection, { UNIT_FIELDS } from '../shared/UnitSection';
import TenancySection, { TENANCY_FIELDS } from '../shared/TenancySection';
import { saveDraft, loadDraft, clearDraft, relativeTime } from '../shared/TenantDraftStorage';

import '../../../ControlPanel/sadmin.css';
import '../../Settings/settings.css';
import '../shared/tenant-form.css';

const SECTIONS = [
    { id: 'brand', label: 'Brand information', anchor: 'section-brand', meta: BRAND_FIELDS },
    { id: 'unit', label: 'Unit information', anchor: 'section-unit', meta: UNIT_FIELDS },
    { id: 'tenancy', label: 'Tenancy & charges', anchor: 'section-tenancy', meta: TENANCY_FIELDS },
];

const initialForm = { tenant_franchisee: 'true' };

/**
 * `/admin/tenant/newtenant/:layout` — single-page Add Tenant.
 *
 * Replaces the legacy 3-step wizard (BrandInfo + UnitInfo + TenancyInfo).
 *
 * - All 35 fields visible on one scrollable page split into 3 sections.
 * - Sticky left-side section nav with per-section completion counters.
 * - Draft autosave to localStorage (debounced 2s); restored via a banner
 *   on next mount.
 * - Validation runs on Submit only; first invalid field scrolls into
 *   view and an error banner counts misses per section.
 * - Confirmation modal via SweetAlert2 (matches V2 flow), then a single
 *   POST /tenant call followed by an optional POST to
 *   /tenantdeliverypartner/createdeliverypartner.
 * - Lookups load via the shared cached useTenantLookups so Add → Edit →
 *   Detail navigation doesn't re-fetch reference data.
 */
const AddNewTenant = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const manageTenants = localStorage.getItem('manageTenants');
    const manageGeneralSettings = localStorage.getItem('manageGeneralSettings');
    const canCreate = manageTenants === '1' || manageGeneralSettings === '1';

    const usertype = localStorage.getItem('usertype');
    const canOverrideLicense = usertype === 'admin' || usertype === 'Backend User' || usertype === 'sadmin';

    const loginId = localStorage.getItem('loginId');
    const loginName = localStorage.getItem('loginName');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [activeSection, setActiveSection] = useState('brand');
    const [submitting, setSubmitting] = useState(false);
    const [draftBanner, setDraftBanner] = useState(null);
    const [draftSavedLabel, setDraftSavedLabel] = useState('');
    const sectionRefs = useRef({});
    const draftTimer = useRef(null);

    const lookups = useTenantLookups({
        countryId: form.tenant_country_id,
        categoryId: form.tenant_category_id,
        propertyId: form.tenant_property_id,
    });

    const concessionaireId = lookups.concessionaireId;

    // ── Auth ────────────────────────────────────────────────────────
    useEffect(() => {
        if (!canCreate) {
            window.alert(t('You are not authorized to view this page'));
            window.history.back();
        }
    }, [canCreate]);

    // ── Draft restore (on mount, if a draft exists) ─────────────────
    useEffect(() => {
        if (!concessionaireId) return;
        const draft = loadDraft(concessionaireId);
        if (draft) setDraftBanner(draft);
    }, [concessionaireId]);

    // ── Draft autosave (debounced 2s) ───────────────────────────────
    useEffect(() => {
        if (!concessionaireId) return;
        // Don't autosave the initial pristine form.
        if (Object.keys(form).length <= 1 && form.tenant_franchisee) return;
        clearTimeout(draftTimer.current);
        draftTimer.current = setTimeout(() => {
            saveDraft(concessionaireId, form);
            setDraftSavedLabel(relativeTime(Date.now(), t));
        }, 2000);
        return () => clearTimeout(draftTimer.current);
    }, [form, concessionaireId]);

    // ── Active section tracking via IntersectionObserver ────────────
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const id = entry.target.id.replace('section-', '');
                        setActiveSection(id);
                    }
                });
            },
            { rootMargin: '-100px 0px -60% 0px', threshold: 0 },
        );
        SECTIONS.forEach((s) => {
            const el = document.getElementById(s.anchor);
            if (el) {
                sectionRefs.current[s.id] = el;
                observer.observe(el);
            }
        });
        return () => observer.disconnect();
    }, []);

    // ── Cmd/Ctrl+S = save ──────────────────────────────────────────
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault();
                handleSubmit();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [form]); // form captured for handleSubmit closure

    // ── Form ops ────────────────────────────────────────────────────
    const onChange = useCallback((field, value) => {
        setForm((prev) => {
            const next = { ...prev, [field]: value };
            // Reset cascading dependents when a parent changes.
            if (field === 'tenant_country_id') {
                next.tenant_property_id = '';
                next.property_mall_code = '';
                next.tenant_floor = '';
            }
            if (field === 'tenant_property_id') {
                next.tenant_floor = '';
            }
            if (field === 'tenant_category_id') {
                next.tenant_subcategory_id = '';
            }
            return next;
        });
        // Clear the matching error as the user fixes it.
        setErrors((prev) => {
            if (!prev[field]) return prev;
            const { [field]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    const restoreDraft = () => {
        if (draftBanner?.form) setForm(draftBanner.form);
        setDraftBanner(null);
    };
    const discardDraft = () => {
        clearDraft(concessionaireId);
        setDraftBanner(null);
    };

    const completionFor = (section) => {
        const filled = section.meta.all.filter((k) => {
            const v = form[k];
            if (v == null || v === '') return false;
            if (Array.isArray(v) && v.length === 0) return false;
            return true;
        }).length;
        const total = section.meta.all.length;
        const requiredFilled = section.meta.required.every((k) => form[k] !== undefined && form[k] !== '' && form[k] !== null);
        return { filled, total, complete: requiredFilled };
    };

    const validate = () => {
        const next = {};
        const allRequired = [...BRAND_FIELDS.required, ...UNIT_FIELDS.required, ...TENANCY_FIELDS.required];
        allRequired.forEach((field) => {
            const v = form[field];
            if (v === undefined || v === '' || v === null) next[field] = t('Required');
        });
        if (form.tenant_store_type === 'Other' && !form.other_store_type) {
            next.other_store_type = t('Specify other store type');
        }
        // Charges are required when present.
        (lookups.charges || []).forEach((_, idx) => {
            const fieldName = `tenant_charge${idx + 1}`;
            if (form[fieldName] === undefined || form[fieldName] === '') next[fieldName] = t('Required');
        });
        // Lease end > start.
        if (form.tenant_lease_startdate && form.tenant_lease_enddate) {
            if (new Date(form.tenant_lease_enddate) <= new Date(form.tenant_lease_startdate)) {
                next.tenant_lease_enddate = t('End date must be after start date');
            }
        }
        // Base rent not zero.
        if (form.tenant_base_rent === '0' || form.tenant_base_rent === 0) {
            next.tenant_base_rent = t('Base rent cannot be zero');
        }
        setErrors(next);
        return next;
    };

    const scrollToFirstError = (errMap) => {
        const fieldOrder = [...BRAND_FIELDS.all, ...UNIT_FIELDS.all, ...TENANCY_FIELDS.all];
        for (const f of fieldOrder) {
            if (errMap[f]) {
                // Section by field membership.
                let sectionId = 'brand';
                if (UNIT_FIELDS.all.includes(f)) sectionId = 'unit';
                else if (TENANCY_FIELDS.all.includes(f)) sectionId = 'tenancy';
                const el = document.getElementById(`section-${sectionId}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return;
            }
        }
    };

    const handleSubmit = async () => {
        const errMap = validate();
        if (Object.keys(errMap).length) {
            scrollToFirstError(errMap);
            return;
        }
        const result = await SweetAlert.fire({
            title: t('Create tenant?'),
            text: t('A new tenant record will be created with the entered details.'),
            cancelButtonText: t('No, keep editing'),
            confirmButtonText: t('Yes, create tenant'),
            reverseButtons: true,
            showCancelButton: true,
        });
        if (!result.isConfirmed) return;

        setSubmitting(true);
        try {
            // 1. Uniqueness check on tenant_code.
            const checkRes = await axios.post(
                `${config.hostname}/tenant/checktcode`,
                { tenant_code: form.tenant_code, tenant_concessionaire_id: concessionaireId },
                { headers },
            );
            if (checkRes.data?.message !== 0 && checkRes.data?.message !== '0') {
                toast.warning(checkRes.data?.message || t('Tenant code already exists'));
                setSubmitting(false);
                return;
            }

            // 2. Look up existing tenant by email to extract API key (existing
            //    backend pattern — multi-property tenants share one API key).
            const emailRes = await axios.post(
                `${config.hostname}/tenant/gettenantsbyemailid`,
                { email_id: form.tenant_email_id, concessionaire_id: concessionaireId },
                { headers },
            );
            const apiKey = emailRes.data?.[0]?.tenant_api_key || '';

            // 3. Build the create payload.
            const payload = {
                ...form,
                tenant_concessionaire_id: concessionaireId,
                tenant_concessionaire_code: lookups.concessionaireCode,
                tenant_mall_code: form.property_mall_code || '',
                tenant_created_date: new Date().toISOString().substr(0, 10),
                tenant_created_by: loginId,
                tenant_login_id: form.tenant_email_id,
                tenant_password: 'egoal@123',
                tenant_disable: false,
                tenant_status: '',
                tenant_api_key: apiKey || '',
                tenant_store_type: form.tenant_store_type === 'Other' ? form.other_store_type : form.tenant_store_type,
                license_override: canOverrideLicense && !!form.license_override,
                login_id: loginId,
                // Charges — copy by index as the legacy contract requires.
                ...Array.from({ length: 5 }).reduce((acc, _, i) => {
                    acc[`tenant_charge${i + 1}`] = form[`tenant_charge${i + 1}`] || 0;
                    return acc;
                }, {}),
            };
            // Strip non-wire keys.
            delete payload.deliveryPartnersId;
            delete payload.other_store_type;
            delete payload.property_mall_code;
            delete payload.tenant_logo; // not yet uploaded — legacy form skipped this too

            const createRes = await axios.post(`${config.hostname}/tenant`, payload, { headers });
            const tenantId = createRes.data?._id;
            toast.success(
                createRes.data?.tenant_license_id
                    ? t('Tenant created. License {{id}} assigned.', { id: createRes.data.tenant_license_id })
                    : t('Tenant created'),
            );

            // 4. Link delivery partners if any were selected.
            if (tenantId && Array.isArray(form.deliveryPartnersId) && form.deliveryPartnersId.length > 0) {
                try {
                    await axios.post(
                        `${config.hostname}/tenantdeliverypartner/createdeliverypartner`,
                        {
                            concessionaire_id: concessionaireId,
                            tenant_id: tenantId,
                            delivery_partner_id: form.deliveryPartnersId,
                            login_name: loginName,
                            login_id: loginId,
                            tenant_store_name: form.tenant_store_name,
                        },
                        { headers },
                    );
                } catch (err) {
                    console.error('delivery partner link failed', err);
                    toast.error(t('Tenant saved, but delivery-partner linking failed'));
                }
            }

            // 5. Clear draft, bust lookup cache (tenant lists may include new
            //    properties / floors), navigate to list.
            clearDraft(concessionaireId);
            invalidateTenantLookups();
            navigate(`${process.env.PUBLIC_URL}/admin/tenant/viewtenant/default`);
        } catch (err) {
            console.error('create tenant failed', err);
            if (err?.response?.status === 409 && err?.response?.data?.code === 'INSUFFICIENT_LICENSES') {
                toast.error(err.response.data.message || t('No licenses available for this property.'));
            } else {
                toast.error(t('Failed to create tenant'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate(`${process.env.PUBLIC_URL}/admin/tenant/viewtenant/default`);
    };

    const scrollTo = (sectionId) => {
        const el = document.getElementById(`section-${sectionId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (!canCreate) return null;

    const errorCount = Object.keys(errors).length;
    const missingPerSection = {
        brand: BRAND_FIELDS.required.filter((f) => errors[f]).length,
        unit: UNIT_FIELDS.required.filter((f) => errors[f]).length + (errors.other_store_type ? 1 : 0),
        tenancy: TENANCY_FIELDS.required.filter((f) => errors[f]).length + Object.keys(errors).filter((k) => k.startsWith('tenant_charge')).length,
    };

    return (
        <Fragment>
            <div className="v3-sadmin">
                {/* Hero ───────────────────────────────────────────── */}
                <div className="v3-sadmin-hero">
                    <div className="v3-sadmin-hero-text">
                        <p className="v3-label" style={{ margin: 0 }}>{t('General settings › Tenants')}</p>
                        <h1 className="v3-display v3-sadmin-greeting">
                            <span className="v3-sadmin-name">{t('Add tenant')}</span>
                        </h1>
                        <p className="v3-body v3-sadmin-sub">
                            {t('Set up brand, unit and tenancy details on a single page. Your entries are auto-saved as you go.')}
                        </p>
                    </div>
                </div>

                {draftBanner && (
                    <div className="v3-tenant-draft-banner">
                        <span className="v3-tenant-draft-text">
                            {t('Unsaved draft from {{time}} — pick up where you left off?', { time: relativeTime(draftBanner.savedAt, t) })}
                        </span>
                        <button type="button" className="v3-btn v3-btn-primary" onClick={restoreDraft}>
                            {t('Restore')}
                        </button>
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={discardDraft}>
                            {t('Discard')}
                        </button>
                    </div>
                )}

                {errorCount > 0 && (
                    <div className="v3-tenant-error-banner">
                        <AlertCircle size={16} />
                        <span>
                            {errorCount === 1 ? t('{{count}} field need attention.', { count: errorCount }) : t('{{count}} fields need attention.', { count: errorCount })}
                            {missingPerSection.brand > 0 && ` ${t('Brand')}: ${missingPerSection.brand}.`}
                            {missingPerSection.unit > 0 && ` ${t('Unit')}: ${missingPerSection.unit}.`}
                            {missingPerSection.tenancy > 0 && ` ${t('Tenancy')}: ${missingPerSection.tenancy}.`}
                        </span>
                    </div>
                )}

                {/* Body ───────────────────────────────────────────── */}
                <div className="v3-tenant-form-layout">
                    {/* Sticky section nav */}
                    <aside className="v3-card v3-tenant-section-nav" aria-label={t('Section navigation')}>
                        {SECTIONS.map((s) => {
                            const { filled, total, complete } = completionFor(s);
                            return (
                                <button
                                    key={s.id}
                                    type="button"
                                    className={`v3-tenant-section-nav-item ${activeSection === s.id ? 'active' : ''} ${complete ? 'complete' : ''}`}
                                    onClick={() => scrollTo(s.id)}
                                >
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                                        <span className="v3-tenant-section-nav-bullet">
                                            {complete ? <Check size={12} /> : null}
                                        </span>
                                        {t(s.label)}
                                    </span>
                                    <span className="v3-tenant-section-nav-count">{filled}/{total}</span>
                                </button>
                            );
                        })}
                        <div className="v3-tenant-section-nav-divider" />
                        <div className="v3-tenant-section-nav-foot">
                            {draftSavedLabel
                                ? t('Draft autosaved {{time}}', { time: draftSavedLabel })
                                : t('Draft autosaves every 2s')}
                        </div>
                    </aside>

                    {/* Form */}
                    <div className="v3-tenant-form-col">
                        <BrandSection
                            mode="add"
                            form={form}
                            errors={errors}
                            onChange={onChange}
                            lookups={lookups}
                        />
                        <UnitSection
                            mode="add"
                            form={form}
                            errors={errors}
                            onChange={onChange}
                            lookups={lookups}
                        />
                        <TenancySection
                            mode="add"
                            form={form}
                            errors={errors}
                            onChange={onChange}
                            lookups={lookups}
                            canOverrideLicense={canOverrideLicense}
                        />
                    </div>
                </div>
            </div>

            {/* Sticky bottom action bar */}
            <div className="v3-tenant-action-bar">
                <span className="v3-tenant-action-summary">
                    {submitting ? t('Creating…') : errorCount > 0 ? (errorCount === 1 ? t('{{count}} field need attention', { count: errorCount }) : t('{{count}} fields need attention', { count: errorCount })) : t('Ready to save')}
                </span>
                <button type="button" className="v3-btn v3-btn-ghost" onClick={handleCancel} disabled={submitting}>
                    <X size={14} /> {t('Cancel')}
                </button>
                <button type="button" className="v3-btn v3-btn-primary" onClick={handleSubmit} disabled={submitting}>
                    <Save size={14} /> {submitting ? t('Creating…') : t('Create tenant')}
                </button>
            </div>
        </Fragment>
    );
};

export default AddNewTenant;
